// "New event" (create appointment) form logic: timezones, time conversion, validation, payload. Pure, no I/O.
import { fmtPhone } from './format.js';

export const TIMEZONES = Object.freeze([
  { value: 'America/New_York', name: 'Eastern' },
  { value: 'America/Chicago', name: 'Central' },
  { value: 'America/Denver', name: 'Mountain' },
  { value: 'America/Phoenix', name: 'Arizona' },
  { value: 'America/Los_Angeles', name: 'Pacific' },
  { value: 'America/Anchorage', name: 'Alaska' },
  { value: 'Pacific/Honolulu', name: 'Hawaii' },
]);

export const REMINDER_OFFSETS = Object.freeze([
  { value: 15, label: '15 minutes' }, { value: 30, label: '30 minutes' }, { value: 60, label: '1 hour' },
  { value: 120, label: '2 hours' }, { value: 1440, label: '1 day' },
]);

export const DEFAULT_MESSAGES = Object.freeze({
  confirmationText: 'Hi #firstname#, your appointment with #myagentname# is confirmed for {date} at {time}. Reply here if you have any questions. #mybusinessphonenumber#',
  reminderText: 'Hi #firstname#, a quick reminder about your appointment with #myagentname# on {date} at {time}. Talk soon!',
  confirmationEmailSubject: 'Your appointment is confirmed — {date} at {time}',
  confirmationEmailBody: 'Hi #firstname#,\n\nYour appointment with #myagentname# is confirmed for {date} at {time}.\n\nIf you need to reschedule, just reply to this email or call #mybusinessphonenumber#.\n\n#myagentname#\n#company#',
});

const pad = (n) => String(n).padStart(2, '0');

/** "Mountain (MST)" / "Mountain (MDT)" for a zone on a given day. */
export function tzLabel(tz, at = new Date()) {
  const base = TIMEZONES.find((z) => z.value === tz);
  let abbr = '';
  try {
    abbr = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(at).find((p) => p.type === 'timeZoneName')?.value || '';
  } catch { /* unknown zone */ }
  const name = base?.name || tz;
  return abbr ? `${name} (${abbr})` : name;
}

/** The zone to preselect: the agent's if we offer it, else the browser's if we offer it, else Mountain. */
export function pickTimezone(agentTz, browserTz) {
  const ok = (z) => TIMEZONES.some((t) => t.value === z);
  if (ok(agentTz)) return agentTz;
  if (ok(browserTz)) return browserTz;
  return 'America/Denver';
}

/**
 * UTC instant for a wall-clock date+time ("2026-11-02", "14:00") in IANA zone `tz`.
 * @returns {Date} Invalid Date when the inputs are malformed.
 */
export function zonedToUtc(dateStr, timeStr, tz) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr || '');
  const t = /^(\d{1,2}):(\d{2})$/.exec(timeStr || '');
  if (!m || !t) return new Date(NaN);
  const [y, mo, d, h, mi] = [+m[1], +m[2], +m[3], +t[1], +t[2]];
  const guess = Date.UTC(y, mo - 1, d, h, mi, 0);
  try {
    const p = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
      .formatToParts(new Date(guess)).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return new Date(guess - (asUtc - guess));
  } catch {
    return new Date(guess);
  }
}

/** Wall-clock date and time of `at` in zone `tz`, rounded UP to the next half hour, plus 30 minutes for the end. */
export function defaultSlot(at, tz) {
  const p = new Intl.DateTimeFormat('en-US', { timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })
    .formatToParts(at).reduce((a2, x) => ({ ...a2, [x.type]: x.value }), {});
  const minutes = (+p.hour) * 60 + (+p.minute);
  const startMin = (minutes < 30 ? 30 : Math.ceil((minutes + 0.001) / 30) * 30); // next half hour boundary after now
  const base = Date.UTC(+p.year, +p.month - 1, +p.day, 0, startMin, 0); // wall-clock arithmetic only (no zone math)
  const start = new Date(base);
  const end = new Date(base + 30 * 60_000);
  const fmtDate = (d) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
  const fmtTime = (d) => `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
  return { date: fmtDate(start), startTime: fmtTime(start), endTime: fmtTime(end), endDate: fmtDate(end) };
}

/** Initial form values for a lead. Start = next half hour (in the event's zone), end = +30 min. */
export function initialForm(lead, { agentTz, browserTz, now = new Date() } = {}) {
  const timezone = pickTimezone(agentTz, browserTz);
  const slot = defaultSlot(now, timezone);
  const name = [lead?.firstName, lead?.lastName].filter(Boolean).join(' ');
  return {
    title: `Appointment with ${name || 'lead'}${lead?.phone ? ` / ${fmtPhone(lead.phone).replace(/^\+1\s?/, '').replace(/[()]/g, '').replace(/\s/g, '-')}` : ''}`,
    description: '', showDescription: false,
    timezone,
    calendar: 'civix',
    date: slot.date, startTime: slot.startTime, endTime: slot.endDate === slot.date ? slot.endTime : '23:59',
    markBusy: true,
    attached: true,
    actionOn: true, dispositionId: '',
    confirmationText: { on: false, body: DEFAULT_MESSAGES.confirmationText },
    reminderText: { on: false, body: DEFAULT_MESSAGES.reminderText, beforeMinutes: 60 },
    confirmationEmail: { on: false, subject: DEFAULT_MESSAGES.confirmationEmailSubject, body: DEFAULT_MESSAGES.confirmationEmailBody },
  };
}

/** Why the form can't be submitted yet, or ''. */
export function eventProblem(f, lead) {
  if (!f.attached) return 'Attach a lead to the event';
  if (!f.title.trim()) return 'Add an event title';
  if (!f.date) return 'Pick a start date';
  if (!f.startTime) return 'Pick a start time';
  if (!f.endTime) return 'Pick an end time';
  const s = zonedToUtc(f.date, f.startTime, f.timezone);
  const e = zonedToUtc(f.date, f.endTime, f.timezone);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return 'Invalid date or time';
  if (e <= s) return 'End time must be after the start time';
  if (f.confirmationText.on && !lead?.phone) return 'This lead has no phone number for the confirmation text';
  if (f.reminderText.on && !lead?.phone) return 'This lead has no phone number for the reminder text';
  if (f.confirmationEmail.on && !lead?.email) return 'This lead has no email for the confirmation email';
  return '';
}

/** True when the form can be submitted (drives the Create Event button). */
export const canCreate = (f, lead) => eventProblem(f, lead) === '';

/** Replace {date}/{time} in a message with the event's date and time in its own zone. */
export function fillWhen(text, startUtc, tz) {
  const date = startUtc.toLocaleDateString('en-US', { timeZone: tz, weekday: 'long', month: 'long', day: 'numeric' });
  const time = startUtc.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
  return String(text || '').replace(/\{date\}/g, date).replace(/\{time\}/g, time);
}

/** Everything the booking helper needs, from the form. */
export function buildEvent(f, lead) {
  const start = zonedToUtc(f.date, f.startTime, f.timezone);
  const end = zonedToUtc(f.date, f.endTime, f.timezone);
  const rem = f.reminderText.on ? new Date(start.getTime() - f.reminderText.beforeMinutes * 60_000) : null;
  return {
    title: f.title.trim(),
    startAt: start.toISOString(),
    endAt: end.toISOString(),
    notes: f.description.trim(),
    timezone: f.timezone,
    busy: f.markBusy,
    syncedTo: f.calendar === 'google' ? ['google'] : [],
    applyDisposition: f.actionOn,
    dispositionId: f.actionOn ? f.dispositionId || '' : '',
    notifications: {
      confirmationText: f.confirmationText.on ? fillWhen(f.confirmationText.body, start, f.timezone) : '',
      reminderText: f.reminderText.on ? { body: fillWhen(f.reminderText.body, start, f.timezone), scheduledFor: rem.toISOString() } : null,
      confirmationEmail: f.confirmationEmail.on
        ? { subject: fillWhen(f.confirmationEmail.subject, start, f.timezone), body: fillWhen(f.confirmationEmail.body, start, f.timezone) }
        : null,
    },
    leadHasPhone: !!lead?.phone,
  };
}

/** Master "All notifications" switch: on when every available notification is on. */
export function allNotificationsOn(f, lead) {
  const flags = [f.actionOn, ...(lead?.phone ? [f.confirmationText.on, f.reminderText.on] : []), ...(lead?.email ? [f.confirmationEmail.on] : [])];
  return flags.length > 0 && flags.every(Boolean);
}

/** Form with every available notification switched on/off together. */
export function setAllNotifications(f, lead, on) {
  return {
    ...f,
    actionOn: on,
    confirmationText: { ...f.confirmationText, on: on && !!lead?.phone },
    reminderText: { ...f.reminderText, on: on && !!lead?.phone },
    confirmationEmail: { ...f.confirmationEmail, on: on && !!lead?.email },
  };
}
